/* eslint-disable max-lines, @typescript-eslint/no-explicit-any */
import { Button, ButtonTheme } from '@cian/button-component';
import { Stars } from '@cian/stars-component';
import { plural } from '@cian/utils/lib/shared/plural';
import * as React from 'react';

import {
  IAgentAvailabilitySchema,
  IAgentReviewsSchema,
  IAgentSchema,
  IKPSchema,
  INewObjectBuilderSchema,
  IOfferSchema,
  IUserSchema,
} from 'shared/types/api-models/offer-card/v1/get-offer-data';

import { Avatar } from '../../../../common/components/avatar';
import { Link } from '../../../../common/components/link';
import { LinkWrapper } from '../../../../common/components/link_wrapper';
import { LegacyCallBackButtonContainer } from '../../../../containers/CallBackButton';
import { CallSourceWishContainer } from '../../../../containers/CallSourceWish';
import { CalltrackingBadgeContainer } from '../../../../containers/CalltrackingBadge';
import { IOfferChatsState } from '../../../../reducers/chat';
import { IUserLevelInfoSchema } from '../../../../repositories/offer-card/entities/agent/UserLevelInfoSchema';
import { IRosreestrInfo } from '../../../../types/offerData';
import {
  getAgentName,
  getAgentType,
  getLifeTime,
  getProfileLink,
  getTrackingLabelPrefix,
  isFromBuilder,
  isHomeowner,
  isHonest,
  isPro,
  mapSkillsArrayIntoTree,
} from '../../../../utils/agent';
import { getFormattedPhoneNumber, getNewbuildingsListQueryStringUrl } from '../../../../utils/formatters';
import { getAgentExperience } from '../../../../utils/getAgentExperience';
import { isAvatarAgentRoundness } from '../../../../utils/isAvatarAgentRoundness';
import { SimpleBotDetector } from '../../../../utils/simpleBotDetector';
import { ABCallButton } from '../../../ab_call_button';
import { PopupOwnOfferContainer } from '../../../popups/own_offer/container';
import { ReviewsDividerIcon } from '../../icons/reviews-divider';
import * as Analytics from '../../tracking';
import { Honest } from '../Honest';
import * as styles from './index.css';

export interface IAuthorProps {
  abGroup: number;
  agent: IAgentSchema | null;
  agentReviews?: IAgentReviewsSchema | null;
  developer: INewObjectBuilderSchema | null;
  offer: IOfferSchema;
  offerChat: IOfferChatsState;
  user: IUserSchema | null;
  agentAvailability: IAgentAvailabilitySchema | null;
  kp: IKPSchema | null;
  showCountrysideBuilderBadge: boolean;
  agentLevelInfo?: IUserLevelInfoSchema | null;
  rosreestrInfo: IRosreestrInfo | null;

  setPhoneCollapse(): void;
  openChat(type?: 'open_phone' | 'open_message'): void;
}

export const Author = ({
  agent,
  agentReviews,
  developer,
  offer,
  user,
  kp,
  showCountrysideBuilderBadge,
  abGroup,
  setPhoneCollapse,
  offerChat,
  agentAvailability,
  agentLevelInfo,
  rosreestrInfo,
  openChat,
}: IAuthorProps) => {
  const { phones } = offer;
  const { isChatsEnabled } = offerChat;
  const [isOwnOfferPopupOpen, setIsOwnOfferPopupOpen] = React.useState<boolean>(false);
  const [isPhonesVisible, setIsPhonesVisible] = React.useState<boolean>(false);
  const [botDetector] = React.useState<SimpleBotDetector>(new SimpleBotDetector());

  const openChatFromPhones = React.useCallback(() => {
    const isAuthenticated = user && user.isAuthenticated;
    const isNotAvailable = Boolean(agentAvailability && !agentAvailability.available);
    const isOfferOwnedByUser = isAuthenticated && user && user.realtyUserId === offer.userId;

    if (isChatsEnabled && isNotAvailable) {
      if (isOfferOwnedByUser) {
        return setIsOwnOfferPopupOpen(true);
      }

      if (isAuthenticated) {
        openChat('open_phone');

        return;
      }

      //tslint:disable-next-line:no-any
      (window as any).waitForLoginPopup?.('chats', () => Promise.resolve(openChat('open_phone')));
    }
  }, [user, agentAvailability, isChatsEnabled, offer, setIsOwnOfferPopupOpen, openChat]);
  const handleClickOnShowPhonesButton = React.useCallback(() => {
    setIsPhonesVisible(true);
  }, [setIsPhonesVisible]);
  const onStartChatButtonClick = React.useCallback(() => {
    const isAgentNotAvailable = agentAvailability && !agentAvailability.available;
    const isAuthenticated = user && user.isAuthenticated;
    const isOfferOwnedByUser = isAuthenticated && user && user.realtyUserId === offer.userId;
    Analytics.trackFirstMessageClick(offer, agent); // <- клик на кнопку!! В любом случае должен отправляться!!
    switch (true) {
      case !isAuthenticated:
        //tslint:disable-next-line:no-any
        (window as any).waitForLoginPopup('chats', () =>
          Promise.resolve(openChat(isAgentNotAvailable ? 'open_message' : undefined)),
        );
        break;
      case isOfferOwnedByUser:
        setIsOwnOfferPopupOpen(true);
        break;
      default:
        openChat(isAgentNotAvailable ? 'open_message' : undefined);
    }
  }, [user, offer, agent, setIsOwnOfferPopupOpen, agentAvailability, openChat]);
  const phoneButton = React.useMemo(() => {
    return offer.status === 'published' ? (
      <Button
        onClick={() => {
          Analytics.trackClickShowPhone({
            offer,
            agent,
            abGroup,
            user,
            kpId: kp && kp.id,
            isBot: !botDetector.isHuman(),
            rosreestrInfo,
          });

          setPhoneCollapse();
          handleClickOnShowPhonesButton();

          openChatFromPhones();
        }}
      >
        <ABCallButton />
      </Button>
    ) : null;
  }, [
    offer,
    agent,
    abGroup,
    user,
    kp,
    botDetector,
    handleClickOnShowPhonesButton,
    openChatFromPhones,
    setPhoneCollapse,
    rosreestrInfo,
  ]);
  const sendMessageButton = React.useMemo(() => {
    return isChatsEnabled ? (
      <Button theme={ButtonTheme.Ghost} onClick={onStartChatButtonClick}>
        Написать сообщение
      </Button>
    ) : null;
  }, [isChatsEnabled, onStartChatButtonClick]);
  const shownPhones = React.useMemo(() => {
    if (!phones || phones.some(phone => !phone.countryCode || !phone.number)) {
      return null;
    }

    return <span className={styles['phones']}>{phones.map(getFormattedPhoneNumber).join(', ')}</span>;
  }, [phones]);
  const description = React.useMemo(() => {
    let descriptionStr: string | null = null;

    if (!agent) {
      return null;
    }

    if (isFromBuilder(offer)) {
      descriptionStr = agent.description;
    } else {
      descriptionStr = mapSkillsArrayIntoTree(agent.skills)
        .map(({ childs, name }) => `${name}: ${childs.map(child => child.name).join(', ')}`)
        .join('. ');
      if (descriptionStr) {
        descriptionStr = `${descriptionStr}.`;
      }
    }

    if (!descriptionStr) {
      return null;
    }

    if (descriptionStr.length > 150) {
      descriptionStr = descriptionStr.substr(0, 150).concat('...');
    }

    return <div className={styles['description']}>{descriptionStr}</div>;
  }, [agent, offer]);

  React.useEffect(() => {
    botDetector.init();

    return () => botDetector.destroy();
  }, []);

  if (!agent) {
    return null;
  }

  const { isCianPartner, isDeveloper, isSubAgent, accountType, status: agentStatus } = agent;

  /**
   * Указывает на сабагента с опубликованным мастер-аккаунтом
   *
   * В таком случае бекенд присылает урл аватара,
   * поэтому вычисляем по косвенному признаку - наличию аватара у субагента
   */
  const isSubagentWithPublishedMaster = isSubAgent && agent.absoluteAvatarUrl;
  const isSpecialist = accountType === 'specialist';
  const isPublishedAgent = agentStatus === 'published';
  const isProAgent = isPro(agent);
  const isHonestAgent = isHonest(agent);
  const isAgentAndPublishedProfile = !isSubAgent && isPublishedAgent;

  const profileLink = getProfileLink(agent, offer, developer);

  const hasReviews = agentReviews && agentReviews.totalCount > 0;
  const hasRate = agentReviews && agentReviews.entityRate && agentReviews.entityRate > 0;
  const rate = (agentReviews && agentReviews.entityRate) || 0;
  const reviewsCount = (agentReviews && agentReviews.totalCount) || 0;

  const name = getAgentName(agent, offer);

  // Если обяъвление загородки привязано к коттеджному поселку,
  // проверяем возможность отображения телефонов у застройщика.
  const shouldRenderContactButtons = kp ? kp.displayBuilderPhone : true;

  const isAvatarRoudness = isAvatarAgentRoundness(offer, agent);

  return (
    <section className={styles['container']} id="author">
      {(isPublishedAgent || isSubagentWithPublishedMaster) && (
        <LinkWrapper
          href={profileLink}
          target="_blank"
          onClick={() => Analytics.trackGoToCard(`${getTrackingLabelPrefix(offer)}ava`)}
          className={styles['link-wrapper']}
        >
          <Avatar
            className={styles['image']}
            isRoundness={isAvatarRoudness}
            url={agent.absoluteAvatarUrl || ''}
            character={(!isHomeowner(offer) && name && name[0]) || ''}
          />
        </LinkWrapper>
      )}
      {((isAgentAndPublishedProfile && agent.accountType) || (agent.isSubAgent && agent.accountType)) && (
        <div className={styles['type']}>{getAgentType(agent, offer)}</div>
      )}
      <Honest
        profileLink={profileLink}
        agent={agent}
        offer={offer}
        isHonestAgent={isHonestAgent}
        isProAgent={isProAgent}
        showCountrysideBuilderBadge={showCountrysideBuilderBadge}
        isCianPartner={isCianPartner}
        agentLevelInfo={agentLevelInfo}
      />
      {isSpecialist && isPublishedAgent && (
        <div className={styles['reviews']}>
          {hasRate && <div className={styles['rate']}>{(rate * 5).toFixed(1).toString().replace('.', ',')}</div>}
          {hasRate && (
            <div className={styles['stars']}>
              <Stars value={rate} percentage uniqueKey={`${agent.id}`} unitClassName={styles['reviews-star']} />
            </div>
          )}
          {(!hasReviews || (hasReviews && !hasRate)) && <div className={styles['no-reviews']}>Нет отзывов</div>}
          {hasReviews && hasRate && (
            <div className={styles['reviews-divider']}>
              <ReviewsDividerIcon />
            </div>
          )}
          {hasReviews && hasRate && (
            <div className={styles['reviews-count']}>
              <Link className={styles['reviews-count-link']} href={`/agents/${agent.id}`}>
                {reviewsCount} {plural(reviewsCount, ['отзыв', 'отзыва', 'отзывов'])}
              </Link>
            </div>
          )}
        </div>
      )}
      <div className={styles['subtitles']}>
        {agent.experience && isSpecialist && <span className={styles['experience']}>{getAgentExperience(agent)}</span>}
        {(getLifeTime(agent) ||
          (!isDeveloper && Boolean(agent.offersCount) && agent.offersLink) ||
          (isDeveloper && developer && developer.stats)) && (
          <div className={styles['subtitle']}>
            <span className={styles['lifetime']}>{getLifeTime(agent)} на ЦИАН</span>
            {!isDeveloper && Boolean(agent.offersCount) && agent.offersLink && (
              <Link
                className={styles['offers-link']}
                href={agent.offersLink}
                target="_blank"
                onClick={() => Analytics.trackGoToCard(`${getTrackingLabelPrefix(offer)}qty`)}
              >
                {agent.offersCount} {plural(Number(agent.offersCount), ['объявление', 'объявления', 'объявлений'])}
              </Link>
            )}
            {isFromBuilder(offer) && agent.isDeveloper && developer && developer.stats && (
              <ul className={styles['company']}>
                {developer.stats.done && developer.stats.done.qs && developer.stats.done.text ? (
                  <li className={styles['company-item']}>
                    <span>Сдано</span>
                    <Link
                      className={styles['company-link']}
                      href={getNewbuildingsListQueryStringUrl(developer.stats.done.qs)}
                      onClick={() => Analytics.trackClickBuilder('to_developerCard_ready_JK')}
                      target="_blank"
                    >
                      {developer.stats.done.text}
                    </Link>
                  </li>
                ) : (
                  <li className={styles['company-item']}>
                    <span>Нет сданных ЖК</span>
                  </li>
                )}
                {developer.stats.progress && developer.stats.progress.qs && developer.stats.progress.text ? (
                  <li className={styles['company-item']}>
                    <span>Строится</span>
                    <Link
                      className={styles['company-link']}
                      href={getNewbuildingsListQueryStringUrl(developer.stats.progress.qs)}
                      onClick={() => Analytics.trackClickBuilder('to_developerCard_in_progress_JK')}
                      target="_blank"
                    >
                      {developer.stats.progress.text}
                    </Link>
                  </li>
                ) : (
                  <li className={styles['company-item']}>
                    <span>Нет строящихся ЖК</span>
                  </li>
                )}
              </ul>
            )}
          </div>
        )}
      </div>
      {description}
      {isFromBuilder(offer) && profileLink && (
        <Link
          className={styles['developer-link']}
          href={profileLink}
          target="_blank"
          rel="nofollow noopener"
          onClick={() => Analytics.trackClickBuilder('to_developerCard_more')}
        >
          Подробнее о застройщике
        </Link>
      )}
      {phones && !phones.some(phone => !phone.countryCode || !phone.number) && shouldRenderContactButtons && (
        <div className={styles['buttons']}>
          {isPhonesVisible ? shownPhones : phoneButton}
          <LegacyCallBackButtonContainer placeType="agent_block" />
          {sendMessageButton}
        </div>
      )}
      {isPhonesVisible && <CalltrackingBadgeContainer />}
      {isPhonesVisible && <CallSourceWishContainer />}

      <PopupOwnOfferContainer isOpen={isOwnOfferPopupOpen} onClose={() => setIsOwnOfferPopupOpen(false)} />
    </section>
  );
};
